/**
 * useMobileAppLifecycle — Capacitor-only bridge for the two app-level
 * lifecycle signals the bundled web app was missing:
 *
 *   1. Deep links. The Android call notification (CallActionReceiver /
 *      IncomingCallActivity) launches `scrile://call/:callId` into
 *      MainActivity, but nothing in the bundle listened for it — the app
 *      opened on whatever screen it was on and the call was unreachable.
 *      Handles both the warm-start `appUrlOpen` event and the cold-start
 *      `App.getLaunchUrl()` (Capacitor does NOT fire appUrlOpen for the
 *      intent that launched the app from a killed state).
 *
 *   2. Resume. Dispatches `triggerNetworkRecovery` so subscribers of the
 *      app:network-recover bus (IncomingCallModal reconcile, Apollo
 *      reconnect hooks, message outbox) re-sync after backgrounding —
 *      the bus existed but had no producers in the shipped bundle.
 *
 * Uses registerPlugin("App") like useNativePush does: the native
 * @capacitor/app plugin ships in mobile-app, no web dependency needed.
 * No-op on web and desktop.
 */

import { useCallback, useEffect, useRef } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { useHistory } from "react-router-dom";
import useAuthUser from "./useAuthUser";
import { triggerNetworkRecovery } from "lib/networkRecovery";

interface AppPlugin {
  addListener(event: "appUrlOpen", handler: (e: { url: string }) => void): Promise<{ remove: () => void }>;
  addListener(event: "resume", handler: () => void): Promise<{ remove: () => void }>;
  getLaunchUrl(): Promise<{ url?: string } | null | undefined>;
}
const CapApp = registerPlugin<AppPlugin>("App");

const isNative = Capacitor.isNativePlatform();

/**
 * scrile://call/42 → /call/42 (и scrilemsg://). Regex parse — `new URL()`
 * host handling of custom schemes varies across WebViews.
 */
export function mapDeepLinkToRoute(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const match = /^scrile(?:msg)?:\/\/([^/?#]+)(?:\/([^?#]*))?/.exec(raw);
  if (!match) return null;
  const host = match[1];
  const rest = (match[2] ?? "").replace(/\/+$/, "");
  switch (host) {
    case "call":
      return rest ? `/call/${rest}` : null;
    case "conference":
      return rest ? `/conference/${rest}` : null;
    case "chat":
    case "messages":
      return rest ? `/messages/${rest}` : null;
    default:
      return null;
  }
}

export default function useMobileAppLifecycle(): void {
  const { user } = useAuthUser();
  const history = useHistory();
  const historyRef = useRef(history);
  historyRef.current = history;
  const userRef = useRef(user);
  userRef.current = user;
  /** Deep link that arrived before auth hydrated (cold start from a killed
   *  app): buffered here and drained by the effect below once `user` lands.
   *  Same pattern as useNativePush's pendingTapRef. */
  const pendingRouteRef = useRef<string | null>(null);
  const lastHandledRef = useRef<{ url: string; at: number } | null>(null);

  // Single navigation policy for deep links (warm appUrlOpen AND the
  // buffered cold-start drain below) — change routing behaviour here only.
  const navigate = useCallback((route: string) => {
    if (historyRef.current.location.pathname !== route) {
      historyRef.current.push(route);
    }
  }, []);

  useEffect(() => {
    if (!isNative) return;
    let cancelled = false;
    const handles: Array<{ remove: () => void }> = [];

    const handleUrl = (url: string | undefined | null) => {
      if (!url) return;
      // getLaunchUrl and the first appUrlOpen can deliver the SAME intent URL
      // on a warm start; also guards a double-tap remounting the call page.
      const prev = lastHandledRef.current;
      if (prev && prev.url === url && Date.now() - prev.at < 3000) return;
      lastHandledRef.current = { url, at: Date.now() };

      const route = mapDeepLinkToRoute(url);
      if (!route) return;
      // eslint-disable-next-line no-console
      console.info("[deep-link] routing", url, "→", route);
      if (userRef.current) {
        navigate(route);
      } else {
        pendingRouteRef.current = route;
      }
    };

    CapApp.addListener("appUrlOpen", (e) => handleUrl(e?.url))
      .then((h) => { if (cancelled) h.remove(); else handles.push(h); })
      .catch(() => { /* plugin unavailable (web/desktop build) */ });
    CapApp.addListener("resume", () => triggerNetworkRecovery("mobile-resume"))
      .then((h) => { if (cancelled) h.remove(); else handles.push(h); })
      .catch(() => { /* ignore */ });
    // Cold start: the launching intent's URL is only available via getLaunchUrl.
    CapApp.getLaunchUrl()
      .then((r) => handleUrl(r?.url))
      .catch(() => { /* ignore */ });

    return () => {
      cancelled = true;
      handles.forEach((h) => {
        try { h.remove(); } catch { /* ignore */ }
      });
    };
  }, [navigate]);

  // Drain the buffered deep link once auth hydrates.
  useEffect(() => {
    if (user && pendingRouteRef.current) {
      const route = pendingRouteRef.current;
      pendingRouteRef.current = null;
      navigate(route);
    }
  }, [user, navigate]);
}
